// File: frontend/src/components/GeneratorTool.js
import React, { useState, useRef } from 'react';
import { BACKEND_URL, supabase } from '../supabaseClient';
import { Toaster, toast } from 'react-hot-toast';
import './GeneratorTool.css';

// Engines & Utils
// import MockupCanvas from './MockupCanvas'; // <--- COMMENTED OUT TO FIX CRASH
import ControlPanel from './ControlPanel';
import { processUpload } from '../utils/imageProcessor';

function GeneratorTool({ session }) {
   
  // State
  const [previewImage, setPreviewImage] = useState(null); // The 1080p version
  const [masterImage, setMasterImage] = useState(null);   // The 3000px version
  const [designImage, setDesignImage] = useState(null);
  
  // Configuration State
  const [config, setConfig] = useState({
    scale: 0.35, 
    rotation: 0,
    x: 0,        
    y: 0,        
    displacementStrength: 20
  });

  const exportFnRef = useRef(null); 

  // --- HANDLERS ---

  const handleUploadBase = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const toastId = toast.loading("Loading image...");
    try {
        const { previewUrl, masterUrl } = await processUpload(file);
        setPreviewImage(previewUrl);
        setMasterImage(masterUrl);
        toast.success("Ready!", { id: toastId });
    } catch (err) {
        console.error("Image processing failed:", err);
        toast.error("Could not load image", { id: toastId });
    }
  };

  const handleUploadDesign = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
          setDesignImage(ev.target.result);
          setConfig(prev => ({ ...prev, scale: 0.35, x: 0, y: 0, rotation: 0 }));
      };
      reader.readAsDataURL(file);
    }
  };

  // --- DOWNLOAD HANDLER ---
  const handleDownload = async () => {
    if (!previewImage || !designImage) {
        toast.error("Please upload both a Product and a Design first.");
        return;
    }

    // 1. Get Fresh Token
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    
    if (!currentSession || !currentSession.access_token) {
        toast.error("Please log in to generate mockups.");
        return;
    }

    const toastId = toast.loading("👨‍🍳 Sending to Server...");

    try {
        const formData = new FormData();
        
        const baseSource = masterImage || previewImage;
        const baseBlob = await fetch(baseSource).then(r => r.blob());
        const designBlob = await fetch(designImage).then(r => r.blob());
        
        formData.append('tshirt', baseBlob, 'tshirt.png');
        formData.append('design', designBlob, 'design.png');
        
        formData.append('config', JSON.stringify({
            scale: config.scale,
            x: config.x,
            y: config.y,
            rotation: config.rotation,
            displacementStrength: config.displacementStrength
        }));

        const response = await fetch(`${BACKEND_URL}/api/generate-mockup?auth_token=${currentSession.access_token}`, {
            method: 'POST',
            headers: { 
                'Authorization': `Bearer ${currentSession.access_token}`
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Server Error: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
            toast.success("Mockup Generated!", { id: toastId });
            // Download logic here if needed
        }

    } catch (err) {
        console.error("Chef Error:", err);
        toast.error(`Failed: ${err.message}`, { id: toastId });
    }
  };

  return (
    <>
      <Toaster position="bottom-right" />
      
      <div className="generator-layout generator-theme">
        <div className="generator-sidebar">
            <div className="control-panel-card">
                <ControlPanel 
                    config={config}
                    setConfig={setConfig}
                    onUploadBase={handleUploadBase}
                    onUploadDesign={handleUploadDesign}
                    onDownload={handleDownload}
                />
            </div>
        </div>

        <div className="generator-workspace">
            <div className="canvas-cage">
                {previewImage ? (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {/* MockupCanvas REMOVED because it was deleted.
                           If you need to use this file again, switch to 'ApparelPage.js' instead.
                        */}
                        <img src={previewImage} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center text-gray-500 animate-pulse">
                        <span style={{ fontSize: '3rem', marginBottom: '1rem' }}>👕</span>
                        <p className="font-bold">Upload Product</p>
                    </div>
                )}
            </div>
        </div>
      </div>
    </>
  );
}

export default GeneratorTool;